import { Fragment, FunctionComponent, CSSProperties, useEffect } from "react";
import { Loader } from "@bracket/react-loader";
import * as ICONS from "@bracket/react-icons";

import "./Button.scss";

type Size = "sm" | "md" | "lg";
type Variant =
	| "primary"
	| "primary-outline"
	| "secondary"
	| "tertiary"
	| "error"
	| "outline"
	| "destructive";

export declare type ButtonProps = {
	onClick?: () => void;
	label: string;
	inner?: FunctionComponent<any>;
	innerProps?: Record<string, any>;
	size?: Size;
	variant?: Variant;
	disabled?: boolean;
	icon?: keyof typeof ICONS;
	iconPosition?: "start" | "end";
	iconProps?: any;
	/** indicate a background process is running by rendering a spinner */
	busy?: boolean;
	mt?: string;
	mb?: string;
	ml?: string;
	mr?: string;
	className?: string;
	width?: string;
	maxWidth?: string;
	flexible?: boolean;
	style?: CSSProperties;
};

export const Button: FunctionComponent<ButtonProps> = ({
	label,
	onClick = () => {},
	inner,
	icon,
	innerProps = {},
	size = "md",
	variant = "primary",
	disabled = false,
	mt = "0",
	mb = "0",
	ml = "0",
	mr = "0",
	className = "",
	width,
	maxWidth,
	busy = false,
	iconPosition = "start",
	iconProps = {},
	flexible,
	style = {},
}) => {
	const Inner = inner || ButtonLabel;

	const classes = () =>
		[
			"button",
			`btn-variant-${variant}`,
			`btn-size-${size}`,
			className,
			busy ? "busy" : null,
			flexible ? "flexible" : null,
		]
			.filter((v) => v)
			.join(" ");

	const styles: CSSProperties = Object.assign(
		{
			marginTop: mt,
			marginBottom: mb,
			marginLeft: ml,
			marginRight: mr,
			width: width || "auto", // Set width to auto if not provided
			maxWidth: maxWidth || "initial",
		},
		style
	);

	const Icon = (
		icon && icon in ICONS ? ICONS[icon] : Fragment
	) as FunctionComponent;

	return (
		<button
			onClick={!disabled ? onClick : undefined}
			className={classes()}
			style={styles}
			disabled={disabled}
		>
			{busy ? (
				<Loader type="ellipsis" />
			) : (
				<Inner label={label} size={size} disabled={disabled} {...innerProps} />
			)}
		</button>
	);
};

const sizeClasses = {
	sm: "text-xsn",
	md: "text-md",
	lg: "text-lg",
};

const ButtonLabel = ({
	label,
	size,
	disabled,
}: {
	label: string;
	size: Size;
	disabled: boolean;
}) => {
	const classes = () => ["font-medium", sizeClasses[size], "label"].join(" ");

	return (
		<div className={classes()} style={{ opacity: disabled ? "0.4" : "1" }}>
			{label}
		</div>
	);
};
